import type { ReploElementType } from "schemas/generated/element";

import * as React from "react";

import { useEditedTimeString } from "@editor/hooks/useEditedTimeString";

import twMerge from "@replo/design-system/utils/twMerge";
import {
  AlignVerticalSpaceAround,
  File,
  Newspaper,
  ShoppingBag,
} from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface PageCardProps {
  pageTitle: string;
  lastEditedAt: string;
  type: ReploElementType;
  isPublished: boolean;
  createdAt: string;
  onClick: () => void;
}

const PageCard: React.FC<PageCardProps> = ({
  pageTitle,
  lastEditedAt,
  type,
  isPublished,
  createdAt,
  onClick,
}) => {
  const { icon: iconToRender, className: iconClassName } = exhaustiveSwitch({
    type,
  })<{
    icon: React.ReactElement;
    className: string;
  }>({
    shopifyProductTemplate: () => ({
      icon: <ShoppingBag size={12} />,
      className: "text-page-template bg-page-template-soft",
    }),
    shopifySection: () => ({
      icon: <AlignVerticalSpaceAround size={12} />,
      className: "text-page-section bg-page-section-soft",
    }),
    shopifyArticle: () => ({
      icon: <Newspaper size={12} />,
      className: "text-page-blog bg-page-blog-soft",
    }),
    page: () => ({
      icon: <File size={12} />,
      className: "text-page-page bg-page-page-soft",
    }),
  });
  const editedTimeString = useEditedTimeString(
    new Date(lastEditedAt),
    new Date(createdAt),
  );
  return (
    <div
      className="border-0.5 border-border w-[270px] rounded-xl shadow-selectable flex flex-col cursor-pointer"
      onClick={onClick}
    >
      <div className="aspect-[16/9] w-full overflow-hidden bg-sheet p-2 pb-0 rounded-t-xl"></div>
      <div className="px-3 py-2 flex gap-x-2">
        {isPublished ? (
          <span className="w-2.5 h-2.5 bg-active rounded-full mt-[5px]" />
        ) : (
          <span className="w-2.5 h-2.5 bg-transparent" />
        )}
        <div className="flex flex-col items-start grow gap-y-1">
          <div className="text-base font-medium text-default truncate flex items-center gap-x-2">
            {pageTitle}
          </div>
          <p className="text-sm text-muted">{editedTimeString}</p>
        </div>
        <div
          className={twMerge(
            "mt-auto h-5 w-5 flex items-center justify-center rounded-md",
            iconClassName,
          )}
        >
          {iconToRender}
        </div>
      </div>
    </div>
  );
};

export default PageCard;
